import PropTypes from 'prop-types';
import { useCallback, useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import theme from '@/common/theme';
import { Col, Row } from '@/components/grid/grid-component';
import Input from '@/components/react-hook-form-inputs/input/input-component';
import SelectSearch from '@/components/react-hook-form-inputs/select-search/select-search-component';
import {
  requiredValidationDropdownMessage,
  requiredValidationMessage,
  urlValidationRules,
} from '@/utils/validations';

import FileUploader from '@/components/react-hook-form-inputs/file-dropzone/file-dropzone-component';
import RichTextEditorInput from '@/components/react-hook-form-inputs/rich-text-input/rich-text-editor';
import CreatableSelect from '@/components/react-hook-form-inputs/select-createable/creatable-search-component';
import CreatePartNumberModal from '@/components/modal/modal-component';
import FormWrapper from '@/components/form-wrapper/form-wrapper-component';
import ImageGallery from '@/components/react-hook-form-inputs/image-gallery/image-gallery-component';
import RevertIconButton from '@/components/revert-icon-button/revert-icon-button';
import OptionsPartNumberForm from './options-part-number-form';
import Styled from './options-form-style';

const StyledFieldWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const StyledRevertButtonWrapper = styled.div`
  position: absolute;
  right: ${(props) => props.$right || '8px'};
  top: ${(props) => props.$top || '50%'};
  transform: translateY(${(props) => props.$translateY || '-50%'});
  z-index: 10;
`;

const flexSpaceBetween = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const styledAddPartNumberRow = {
  ...flexSpaceBetween,
  marginLeft: '0',
};

const styledMediaRow = {
  ...flexSpaceBetween,
  margin: '0',
};

/**
 * Reusable Option Form Fields Component
 * @param {object} props - Component props
 * @param {string} props.fieldPrefix - The prefix for field names (e.g., 'option', 'variant.0', etc.)
 * @param {boolean} props.isLoading - Loading state
 * @param {object} props.defaultValues - Default values to populate from
 * @param {object} props.pricelistData - Pricelist data
 * @param {boolean} props.pricelistLoading - Pricelist loading state
 * @param {Function} props.refetchPriceList - Function to refetch pricelist data
 * @param {object} props.originalOptions - The original options.
 * @param {Function} props.nested - Is this nested in another form.
 * @returns {JSX.Element}
 */
const OptionFormFields = ({
  fieldPrefix = 'option',
  isLoading = false,
  defaultValues = {},
  pricelistData = [],
  pricelistLoading = false,
  refetchPriceList = () => {},
  originalOptions = {},
  nested = false,
}) => {
  const { id: companyId } = useParams();

  const paddingLeft =
    fieldPrefix === 'option' ? '0px 15px 0px 0px !important' : 'inherit';

  const [optionStatus, setOptionStatus] = useState('');
  const [modalAction, setModalAction] = useState({ label: '', id: null });

  const { watch, getValues, setValue, clearErrors } = useFormContext();

  const PartNumberSelect = !nested ? CreatableSelect : SelectSearch;

  // Helper function to get field name with prefix
  const getFieldName = useCallback(
    (fieldName) => (fieldPrefix ? `${fieldPrefix}.${fieldName}` : fieldName),
    [fieldPrefix],
  );

  const getFieldPrefix = useCallback(
    (_fieldPrefix) =>
      !_fieldPrefix || _fieldPrefix === 'option'
        ? 'prices'
        : `${_fieldPrefix}.prices`,
    [],
  );

  // Helper function to check if field can be reverted (only for variants)
  const canRevert = useCallback(
    (fieldName) => {
      if (!fieldPrefix || fieldPrefix === 'option') return false;

      const overrides = watch(`${fieldPrefix}.overrides`) || [];
      return overrides.includes(fieldName);
    },
    [fieldPrefix, watch],
  );

  // Helper function to update overrides array
  const updateOverrides = useCallback(
    (fieldName, shouldAdd) => {
      if (!fieldPrefix || fieldPrefix === 'option') return;

      const currentValues = getValues();
      const overrides = currentValues[fieldPrefix]?.overrides || [];

      if (shouldAdd && !overrides.includes(fieldName)) {
        setValue(getFieldName('overrides'), [...overrides, fieldName]);
      } else if (!shouldAdd && overrides.includes(fieldName)) {
        const filteredOverrides = overrides.filter((o) => o !== fieldName);
        setValue(getFieldName('overrides'), filteredOverrides);
      }
    },
    [fieldPrefix, getValues, setValue, getFieldName],
  );

  // Helper function to check if field value differs from original
  const isFieldOverridden = useCallback(
    (fieldName, currentValue) => {
      if (!fieldPrefix || fieldPrefix === 'option') return false;

      const currentValues = getValues();
      const originalValue = currentValues.option?.[fieldName];

      // Special handling for selectedPriceId
      if (fieldName === 'selectedPriceId') {
        return currentValue !== originalValue;
      }

      // For other fields, compare values
      return currentValue !== originalValue;
    },
    [fieldPrefix, getValues],
  );

  // Function to revert a field back to original value
  const handleRevert = useCallback(
    (fieldName) => {
      if (!fieldPrefix || fieldPrefix === 'option') return;

      const originalValue = originalOptions?.[fieldName];

      if (fieldName === 'selectedPriceId') {
        // Handle part number revert specially
        const pricesFieldName = getFieldPrefix(fieldPrefix);
        const originalPriceEntry = originalOptions?.parts?.[0];

        if (originalPriceEntry) {
          setValue(pricesFieldName, [originalPriceEntry]);
          setValue(getFieldName('selectedPriceId'), originalValue);

          // Update part number related fields
          const selectedPart = pricelistData?.find(
            (part) => part.id === originalValue,
          );
          if (selectedPart) {
            setValue(getFieldName('part_number'), selectedPart.part_number);
            setValue(getFieldName('temp_pn'), selectedPart.temp_pn);
          }
        }
      } else {
        // Revert regular field
        setValue(getFieldName(fieldName), originalValue, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
      // Remove from overrides
      updateOverrides(fieldName, false);
    },
    [
      fieldPrefix,
      setValue,
      getFieldName,
      getFieldPrefix,
      pricelistData,
      updateOverrides,
      originalOptions,
    ],
  );

  // Function to propagate parent changes to variants that don't override the field
  const propagateToVariants = useCallback(
    (fieldName, newValue) => {
      // Only propagate if we're editing the main option (not a variant)
      if (fieldPrefix !== 'option') return;

      const currentValues = getValues();
      const variants = currentValues.variant || [];

      variants.forEach((variant, index) => {
        const overrides = variant.overrides || [];

        // Only update if this field is not in the variant's overrides
        if (!overrides.includes(fieldName)) {
          if (fieldName === 'selectedPriceId') {
            // Handle part number selection specially
            const selectedPart = pricelistData?.find(
              (part) => part.id === newValue,
            );
            if (selectedPart) {
              setValue(`variant.${index}.selectedPriceId`, selectedPart.id);
              setValue(
                `variant.${index}.part_number`,
                selectedPart.part_number,
              );
              setValue(`variant.${index}.temp_pn`, selectedPart.temp_pn);
            }
          } else {
            setValue(`variant.${index}.${fieldName}`, newValue);
          }
        }
      });
    },
    [fieldPrefix, getValues, setValue, pricelistData],
  );

  // Enhanced blur handler that checks for overrides and updates override state
  const createBlurHandler = useCallback(
    (fieldName) => (event) => {
      const newValue = event.target ? event.target.value : event;

      // Check if field is overridden and update overrides array
      if (fieldPrefix && fieldPrefix !== 'option') {
        const isOverridden = isFieldOverridden(fieldName, newValue);
        updateOverrides(fieldName, isOverridden);
      }

      // Only propagate to variants when we're in the main option form context
      if (fieldPrefix === 'option') {
        propagateToVariants(fieldName, newValue);
      }
    },
    [fieldPrefix, isFieldOverridden, updateOverrides, propagateToVariants],
  );

  // Enhanced rich text blur handler
  const createRichTextBlurHandler = useCallback(
    (fieldName) => (value) => {
      // Check if field is overridden and update overrides array
      if (fieldPrefix && fieldPrefix !== 'option') {
        const isOverridden = isFieldOverridden(fieldName, value);
        updateOverrides(fieldName, isOverridden);
      }

      // Only propagate to variants when we're in the main option form context
      if (fieldPrefix === 'option') {
        propagateToVariants(fieldName, value);
      }
    },
    [fieldPrefix, isFieldOverridden, updateOverrides, propagateToVariants],
  );

  // Enhanced file change handler
  const createFileChangeHandler = useCallback(
    (fieldName) => (files) => {
      // Set the form value first
      setValue(getFieldName(fieldName), files, {
        shouldValidate: true,
        shouldDirty: true,
      });

      // Check if field is overridden and update overrides array
      if (fieldPrefix && fieldPrefix !== 'option') {
        const isOverridden = isFieldOverridden(fieldName, files);
        updateOverrides(fieldName, isOverridden);
      }

      // Only propagate to variants when we're in the main option form context
      // Don't propagate when we're editing a variant (which has its own FormProvider)
      if (fieldPrefix === 'option') {
        propagateToVariants(fieldName, files);
      }
    },
    [
      fieldPrefix,
      isFieldOverridden,
      updateOverrides,
      propagateToVariants,
      setValue,
      getFieldName,
    ],
  );

  // Single select handler
  const handleSingleSelectChange = (selectedOption) => {
    const pricesFieldName = getFieldPrefix(fieldPrefix);

    if (selectedOption) {
      const priceEntry = {
        price_id: selectedOption.value,
        description:
          pricelistData?.find((part) => part.id === selectedOption.value)
            ?.name || '',
      };

      setValue(pricesFieldName, [priceEntry], {
        shouldValidate: true,
        shouldDirty: true,
      });
    } else {
      setValue(pricesFieldName, [], {
        shouldValidate: true,
        shouldDirty: true,
      });
    }

    // Check if field is overridden and update overrides array
    if (fieldPrefix && fieldPrefix !== 'option') {
      const isOverridden = isFieldOverridden(
        'selectedPriceId',
        selectedOption?.value,
      );
      updateOverrides('selectedPriceId', isOverridden);
    }

    // Only propagate to variants when we're in the main option form context
    if (fieldPrefix === 'option') {
      propagateToVariants('selectedPriceId', selectedOption?.value);
    }
  };

  const getPricingOptions = () => {
    if (pricelistData) {
      return pricelistData.map((part) => ({
        value: part.id,
        label: part.part_number,
      }));
    }
    // Fallback to current prices if pricelistData not loaded yet
    const pricesFieldName = getFieldPrefix(fieldPrefix);
    return (
      getValues(pricesFieldName)?.map((part) => ({
        value: part.price_id,
        label: part.part_number || part.description,
      })) || []
    );
  };

  const handlePartNumberCreated = (newPartNumber) => {
    const pricesFieldName = getFieldPrefix(fieldPrefix);

    // For single select, just set the newly created part number as selected
    const newPriceEntry = {
      price_id: newPartNumber.id,
      description: newPartNumber.name,
    };

    // Update form fields - single select gets single values
    setValue(pricesFieldName, [newPriceEntry], {
      shouldValidate: true,
      shouldDirty: true,
    });

    setValue(getFieldName('selectedPriceId'), newPartNumber.id, {
      shouldValidate: true,
      shouldDirty: true,
    });

    // Clear validation errors
    clearErrors([pricesFieldName, getFieldName('selectedPriceId')]);

    // Refetch the data to get updated list from server
    refetchPriceList();
  };

  // Initial fetch of part numbers
  useEffect(() => {
    if (companyId) {
      refetchPriceList();
    }
  }, [refetchPriceList, companyId]);

  // Populate default values when provided
  useEffect(() => {
    if (defaultValues && Object.keys(defaultValues).length > 0) {
      Object.entries(defaultValues).forEach(([key, value]) => {
        setValue(getFieldName(key), value, {
          shouldValidate: false,
          shouldDirty: false,
        });
      });
    }
  }, [defaultValues, setValue, fieldPrefix, getFieldName]);

  const optionStatusInitialColor = watch(getFieldName('status'));

  const getColor = () => {
    switch (optionStatus || optionStatusInitialColor) {
      case 'active':
        return theme.COLOR_STATUS_GREEN;
      case 'inactive':
        return theme.COLOR_STATUS_BLUE;
      default:
        return null;
    }
  };

  const closeCreatePartNumberModal = () => {
    setModalAction({ label: '', id: null });
  };

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
  ];

  // Helper component to wrap fields with revert button
  const FieldWithRevert = useCallback(
    ({ children, fieldName, tooltipMessage, right, top, translateY }) => {
      const showRevert = canRevert(fieldName);

      return (
        <StyledFieldWrapper>
          {children}
          {showRevert && (
            <StyledRevertButtonWrapper
              $right={right}
              $top={top}
              $translateY={translateY}
            >
              <RevertIconButton
                onClick={() => handleRevert(fieldName)}
                tooltipMessage={tooltipMessage}
              />
            </StyledRevertButtonWrapper>
          )}
        </StyledFieldWrapper>
      );
    },
    [canRevert, handleRevert],
  );

  return (
    <>
      <Row justify="center" $margin="0px !important">
        <Col $padding={paddingLeft} xs={8} sm={8} md={8} lg={8} xl={8} xxl={8}>
          <Col $padding={paddingLeft} sm={12} md={12} lg={12} xl={12} xxl={12}>
            <FieldWithRevert fieldName="name" tooltipMessage="Revert Name">
              <Input
                disabled={isLoading}
                label="Option Name"
                placeholder="Option Name"
                name={getFieldName('name')}
                registerOptions={{
                  required: requiredValidationMessage,
                  onBlur: createBlurHandler('name'),
                }}
              />
            </FieldWithRevert>
          </Col>

          <Row style={styledAddPartNumberRow} $marginBottom="0px">
            <Col $padding={paddingLeft} sm={12} md={12} lg={6} xl={6} xxl={6}>
              <FieldWithRevert
                fieldName="selectedPriceId"
                tooltipMessage="Revert Part Number"
                right="65px"
                top="calc(50% - 5px)"
                translateY="-50%"
              >
                <PartNumberSelect
                  header="Part Number"
                  placeholder="Please select a part number"
                  formatCreateLabel="Add Part Number"
                  options={getPricingOptions()}
                  name={getFieldName('selectedPriceId')}
                  cy={`sel${fieldPrefix}PartNumbers`}
                  required={{
                    required: 'Please select a part number',
                  }}
                  onChange={handleSingleSelectChange}
                  onCreateOption={() => {
                    setModalAction({ label: 'Add', id: null });
                  }}
                  isLoading={isLoading || pricelistLoading}
                  menuPlacement="bottom"
                  maxMenuHeight={250}
                  hideSelectedOptions={false}
                />
              </FieldWithRevert>
            </Col>

            <Col sm={12} md={12} lg={6} xl={6} xxl={6}>
              <FieldWithRevert
                fieldName="status"
                tooltipMessage="Revert Status"
                right="65px"
                top="calc(50% - 5px)"
                translateY="-50%"
              >
                <SelectSearch
                  cy={`sel${fieldPrefix}Status`}
                  header="Status"
                  name={getFieldName('status')}
                  label="Please select a status"
                  options={statusOptions}
                  icon={<Styled.StatusCircle color={getColor()} />}
                  required={{
                    required: requiredValidationDropdownMessage,
                    onChange: (e) => {
                      setOptionStatus(e.target.value);

                      // Check if field is overridden and update overrides array
                      if (fieldPrefix && fieldPrefix !== 'option') {
                        const isOverridden = isFieldOverridden(
                          'status',
                          e.target.value,
                        );
                        updateOverrides('status', isOverridden);
                      }

                      // Only propagate to variants when we're in the main option form context
                      if (fieldPrefix === 'option') {
                        propagateToVariants('status', e.target.value);
                      }
                    },
                  }}
                  isLoading={isLoading}
                />
              </FieldWithRevert>
            </Col>
          </Row>

          <Col $padding={paddingLeft} sm={12} md={12} lg={12} xl={12} xxl={12}>
            <FieldWithRevert
              fieldName="description"
              tooltipMessage="Revert Frontend Description"
            >
              <RichTextEditorInput
                label="Frontend Description"
                name={getFieldName('description')}
                view={false}
                newVersion
                marginBottom="25px"
                onBlur={createRichTextBlurHandler('description')}
              />
            </FieldWithRevert>
          </Col>
          <Col $padding={paddingLeft} sm={12} md={12} lg={12} xl={12} xxl={12}>
            <FieldWithRevert
              fieldName="quote_description"
              tooltipMessage="Revert Quote Description"
            >
              <RichTextEditorInput
                label="Quote Description"
                name={getFieldName('quote_description')}
                view={false}
                newVersion
                marginBottom="25px"
                onBlur={createRichTextBlurHandler('quote_description')}
              />
            </FieldWithRevert>
          </Col>

          <Row style={styledMediaRow}>
            <Col $padding={paddingLeft} sm={6} md={6} lg={6} xl={6} xxl={6}>
              <FieldWithRevert
                fieldName="brochure_link"
                tooltipMessage="Revert Brochure Link"
              >
                <Input
                  disabled={isLoading}
                  label="Brochure Link"
                  placeholder="https://"
                  name={getFieldName('brochure_link')}
                  registerOptions={{
                    validate: (value) => {
                      if (!value) return true; // Allow empty value
                      let errorMessage = null;
                      urlValidationRules.forEach((rule) => {
                        if (!rule.pattern.test(value)) {
                          errorMessage = rule.message;
                        }
                      });
                      return errorMessage || true;
                    },
                    onBlur: createBlurHandler('brochure_link'),
                  }}
                />
              </FieldWithRevert>
            </Col>
            <Col sm={6} md={6} lg={6} xl={6} xxl={6}>
              <FieldWithRevert
                fieldName="video_link"
                tooltipMessage="Revert Video Link"
              >
                <Input
                  disabled={isLoading}
                  label="Video Link"
                  placeholder="https://"
                  name={getFieldName('video_link')}
                  registerOptions={{
                    validate: (value) => {
                      if (!value) return true; // Allow empty value
                      let errorMessage = null;
                      urlValidationRules.forEach((rule) => {
                        if (!rule.pattern.test(value)) {
                          errorMessage = rule.message;
                        }
                      });
                      return errorMessage || true;
                    },
                    onBlur: createBlurHandler('video_link'),
                  }}
                />
              </FieldWithRevert>
            </Col>
          </Row>
        </Col>

        <Col sm={4} md={4} lg={4} xl={4} xxl={4}>
          <FileUploader
            headerText="Upload Thumbnail"
            name={getFieldName('thumbnail')}
            onChange={createFileChangeHandler('thumbnail')}
            showRevert={canRevert('thumbnail')}
            onRevert={() => handleRevert('thumbnail')}
          />

          <FieldWithRevert fieldName="media" tooltipMessage="Revert Gallery">
            <ImageGallery
              headerText="Upload Gallery"
              name={getFieldName('media')}
              margin="10px 0 0 0"
              onChange={createFileChangeHandler('media')}
              description="Add Option Gallery Images"
            />
          </FieldWithRevert>
        </Col>
      </Row>

      <CreatePartNumberModal
        open={!!modalAction.label}
        close={closeCreatePartNumberModal}
        width="30%"
        alignItems="flex-start"
        title={`${modalAction.label} Part Number`}
        titleFontSize="28px"
        bodyStyle={{ width: '100%', padding: 0 }}
        loading={isLoading}
        minHeight="400px"
      >
        <FormWrapper onSubmit={() => {}}>
          <OptionsPartNumberForm
            setModalAction={setModalAction}
            modalAction={modalAction}
            loading={isLoading}
            companyId={companyId}
            onPartNumberCreated={handlePartNumberCreated}
          />
        </FormWrapper>
      </CreatePartNumberModal>
    </>
  );
};

OptionFormFields.defaultProps = {
  fieldPrefix: 'option',
  isLoading: false,
  defaultValues: {},
  pricelistData: [],
  pricelistLoading: false,
  refetchPriceList: () => {},
  originalOptions: {},
  nested: false,
};

OptionFormFields.propTypes = {
  fieldPrefix: PropTypes.string,
  isLoading: PropTypes.bool,
  defaultValues: PropTypes.object,
  pricelistData: PropTypes.array,
  pricelistLoading: PropTypes.bool,
  refetchPriceList: PropTypes.func,
  originalOptions: PropTypes.object,
  nested: PropTypes.bool,
};

export default OptionFormFields;
